import * as Sentry from '@sentry/browser';
import { moduleMetadataIntegration, type ErrorEvent } from '@sentry/browser';
import { SentryConfig } from '@/interfaces/SentryConfig';
/** Host bundles, CDN MFE chunks, and federation exposes count as first-party. */
const FIRST_PARTY_FRAME_FILENAME_MARKERS = [
    '/Scripts/',
    '/micro-frontends/',
    '__federation_expose_',
];

function isFirstPartyFrameFilename(filename: string): boolean {
    return FIRST_PARTY_FRAME_FILENAME_MARKERS.some((marker) => filename.includes(marker));
}

const NETWORK_CONNECTIVITY_ERROR_MESSAGE_PARTIALS = [
    'NetworkError when attempting to fetch resource',
    'Failed to fetch',
    'ERR_NETWORK',
    'ERR_INTERNET_DISCONNECTED',
];

const EXTENSION_URL_PREFIXES = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'safari-web-extension://',
];

const PENDO_DEPENDENCY_TOKENS = [
    'cdn.pendo.io',
    'pendo-agent',
    'pendo',
];

const PENDO_ASSIST_HOST_TOKENS = [
    '.assist.chromeriver.com',
];

const PENDO_UI_BREADCRUMB_TOKENS = [
    'pendo-',
    'pendo-resource-center',
    'pendo-close-guide',
    'pendo-text-input',
];

// ---------------------------------------------------------------------------
// ignoreErrors
// ---------------------------------------------------------------------------
// Matched before the event is fully built — more efficient than beforeSend.
// Use for pure string/pattern matches against the error message or type where
// no stack frame analysis is needed.
const IGNORE_ERRORS: Array<string | RegExp> = [
    // Cross-origin scripts with no CORS headers produce "Script error." with no
    // stack or detail. The browser deliberately withholds the information, so
    // there is nothing actionable. One of the most common Sentry noise sources
    // across all web apps.
    /^Script error\.?$/i,

    // Browser-level resize observation notifications — not application logic.
    // Chrome and Firefox use distinct messages for the same underlying event.
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',

    // Rspack/webpack chunk and CSS chunk loading failures are network or
    // deployment infrastructure problems, not app bugs. They produce high
    // event volume with no actionable stack. Lifting here avoids building
    // the event entirely (previously caught inline in beforeSend).
    /Loading chunk \d+ failed/,
    /Loading CSS chunk \d+ failed/,
    'ChunkLoadError',

    // Non-Error rejections with undefined/null values are browser or
    // third-party library timing noise. The SDK captures them when a promise
    // is rejected with a plain object or undefined instead of an Error.
    // Covers both the promise-rejection and exception-capture SDK variants.
    // Sentry v9 changed the message format for object rejections from
    // "Non-Error promise rejection captured with keys" to
    // "Object captured as promise rejection with keys" — both patterns are listed.
    /Non-Error (promise rejection|exception) captured with (keys|value)/,
    /Object captured as promise rejection with keys/,

    // Network errors — also handled in beforeSend, but ignoreErrors fires
    // earlier (before event is fully built) and is more reliable for
    // directly-thrown errors in Sentry v9.
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'ERR_NETWORK',
    'ERR_INTERNET_DISCONNECTED'
];

// ---------------------------------------------------------------------------
// denyUrls
// ---------------------------------------------------------------------------
// Errors originating from these URL patterns are dropped before any processing.
// Covers browser extensions across all major browsers and known third-party
// scripts that generate unactionable client-side noise.
const DENY_URLS: RegExp[] = [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,        // Firefox extensions — previously only caught in beforeSend
    /^safari-extension:\/\//i,
    /^file:\/\//i,
    /^webkit-masked-url:\/\//i,
    /cdn\.pendo\.io/i,
    /pendo.*\.js/i,
];

// ---------------------------------------------------------------------------
// beforeSend helpers
// ---------------------------------------------------------------------------

/**
 * Network connectivity errors are outside app control — the user is offline or
 * hitting infrastructure-level failures. They generate high volume with no
 * actionable signal for the dev team.
 *
 * Checks both event.message (console-captured events) AND the exception value
 * (thrown errors), because Failed to fetch / NetworkError commonly surfaces as
 * either depending on how the rejection propagates through the call stack.
 */
function isNetworkConnectivityError(event: ErrorEvent): boolean {
    const message = event.message || '';
    const exceptionMessage = event.exception?.values?.[0]?.value || '';
    return NETWORK_CONNECTIVITY_ERROR_MESSAGE_PARTIALS.some(p => message.includes(p) || exceptionMessage.includes(p));
}

/**
 * ASP.NET PageRequestManager (UpdatePanel) and HTTP gateway errors originate
 * from the .NET WebForms partial-postback infrastructure, not app JavaScript.
 * They are server-side or network-layer failures that produce client-side noise.
 *
 * Timeout is only filtered when a PageRequestManager frame is present to avoid
 * accidentally dropping legitimate app-level timeout errors from other code paths.
 * HTTP gateway errors (502/503/504) are only matched on their specific gateway
 * message strings to preserve application-originated HTTP errors.
 */
function isPageRequestManagerNoise(event: ErrorEvent): boolean {
    const errorValue = event.exception?.values?.[0];
    const errorMessage = errorValue?.value || '';
    const stackFrames = errorValue?.stacktrace?.frames || [];

    const hasPageRequestManagerFrame = stackFrames.some(frame =>
        frame.function?.includes('PageRequestManager') ||
        frame.function?.includes('XMLHttpExecutor') ||
        frame.filename?.includes('ScriptResource') ||
        frame.filename?.includes('WebResource')
    );

    if (!hasPageRequestManagerFrame) return false;

    return (
        errorMessage.includes('The server request timed out') ||
        errorMessage.includes('An unknown error occurred while processing the request on the server') ||
        errorMessage.includes('PageRequestManagerParserErrorException') ||
        (errorMessage.includes('502') && errorMessage.includes('Bad Gateway')) ||
        (errorMessage.includes('503') && errorMessage.includes('Service Unavailable')) ||
        (errorMessage.includes('504') && errorMessage.includes('Gateway Timeout'))
    );
}

/**
 * denyUrls blocks errors that *originate from* extension URLs, but some
 * extension-injected errors have mixed stacks where only certain frames have
 * extension URLs — those slip through denyUrls. Drop any event that has an
 * extension frame anywhere in the stack, regardless of where the error originated.
 */
function isExtensionError(event: ErrorEvent): boolean {
    const stackFrames = event.exception?.values?.[0]?.stacktrace?.frames || [];

    return stackFrames.some(frame =>
        EXTENSION_URL_PREFIXES.some(prefix => frame.filename?.startsWith(prefix))
    );
}

function hasPendoToken(value: string | undefined): boolean {
    if (!value) return false;
    const normalizedValue = value.toLowerCase();
    return PENDO_DEPENDENCY_TOKENS.some(token => normalizedValue.includes(token));
}

/**
 * Drop any event where at least one stack frame indicates a Pendo dependency.
 * This intentionally applies an "any frame" rule to catch mixed stacks where
 * only part of the execution chain originates from injected Pendo scripts.
 */
function isPendoDependencyFrameError(event: ErrorEvent): boolean {
    const values = event.exception?.values || [];

    return values.some(exceptionValue => {
        const stackFrames = exceptionValue.stacktrace?.frames || [];
        return stackFrames.some(frame =>
            hasPendoToken(frame.filename) ||
            hasPendoToken(frame.function)
        );
    });
}

/**
 * Drop any event where at least one stack frame originates from an Assist
 * script host. This mirrors the Pendo frame filter so mixed stacks are still
 * dropped when the third-party Assist script appears anywhere in the chain.
 */
function isAssistDependencyFrameError(event: ErrorEvent): boolean {
    const values = event.exception?.values || [];

    return values.some(exceptionValue => {
        const stackFrames = exceptionValue.stacktrace?.frames || [];
        return stackFrames.some(frame => hasAssistHostToken(frame.filename));
    });
}

function hasAssistHostToken(value: string | undefined): boolean {
    if (!value) return false;
    const normalizedValue = value.toLowerCase();
    return PENDO_ASSIST_HOST_TOKENS.some(token => normalizedValue.includes(token));
}

function getRecentBreadcrumbs(event: ErrorEvent): NonNullable<ErrorEvent['breadcrumbs']> {
    return (event.breadcrumbs || []).slice(-2);
}

function hasAssistHostBreadcrumb(event: ErrorEvent): boolean {
    const breadcrumbs = getRecentBreadcrumbs(event);
    return breadcrumbs.some(breadcrumb => {
        const url = breadcrumb.data?.url;
        const message = breadcrumb.message;
        return hasAssistHostToken(typeof url === 'string' ? url : undefined) ||
            hasAssistHostToken(message);
    });
}

function hasPendoUiBreadcrumb(event: ErrorEvent): boolean {
    const breadcrumbs = getRecentBreadcrumbs(event);
    return breadcrumbs.some(breadcrumb => {
        const message = (breadcrumb.message || '').toLowerCase();
        const category = (breadcrumb.category || '').toLowerCase();
        return PENDO_UI_BREADCRUMB_TOKENS.some(token =>
            message.includes(token) || category.includes(token)
        );
    });
}

/**
 * Pendo/Assist breadcrumb-based noise filter:
 * drop when one of the last two event breadcrumbs shows either a Pendo UI
 * interaction trail OR a *.assist.chromeriver.com fetch. This keeps the
 * filter tied to recent activity instead of older breadcrumb history.
 */
function isLikelyPendoAssistNoise(event: ErrorEvent): boolean {
    return hasAssistHostBreadcrumb(event) || hasPendoUiBreadcrumb(event);
}

/**
 * Rspack/webpack chunk and CSS chunk loading failures are network or deployment
 * infrastructure problems, not app bugs. Also matches ChunkLoadError by name.
 * ignoreErrors handles the regex match as a first pass, but Sentry v9 is
 * unreliable for window.onerror events — beforeSend is the safety net.
 */
function isChunkLoadError(event: ErrorEvent): boolean {
    const message = event.message || event.exception?.values?.[0]?.value || '';
    const type = event.exception?.values?.[0]?.type || '';
    return (
        type === 'ChunkLoadError' ||
        message === 'ChunkLoadError' ||
        /Loading chunk \d+ failed/.test(message) ||
        /Loading CSS chunk \d+ failed/.test(message)
    );
}

/**
 * ResizeObserver loop notifications are browser-level events, not application
 * logic. Chrome and Firefox use distinct messages for the same underlying event.
 * These are emitted natively with no stack frames, which causes ignoreErrors
 * string matching to be unreliable in Sentry v9 — beforeSend is the safety net.
 */
function isResizeObserverNoise(event: ErrorEvent): boolean {
    const message = event.message || event.exception?.values?.[0]?.value || '';
    return (
        message.includes('ResizeObserver loop limit exceeded') ||
        message.includes('ResizeObserver loop completed with undelivered notifications')
    );
}

/**
 * Non-Error and plain-object promise rejections have no actionable stack and
 * are browser/library timing noise. Sentry v9 changed the message format for
 * object rejections from "Non-Error promise rejection captured with keys" to
 * "Object captured as promise rejection with keys" — both are covered here.
 * Also handles rejections with undefined/null values.
 *
 * ignoreErrors covers these as a first pass, but beforeSend is the reliable
 * safety net for unhandled rejection events that bypass ignoreErrors in v9.
 */
function isNonErrorRejectionNoise(event: ErrorEvent): boolean {
    const message = event.message || event.exception?.values?.[0]?.value || '';
    return (
        /Non-Error (promise rejection|exception) captured with (keys|value)/.test(message) ||
        /Object captured as promise rejection with keys/.test(message)
    );
}

/**
 * When a promise is rejected with a CustomEvent object (common in browser
 * event system libraries and WebForms postback handlers), the SDK wraps it into
 * a promise rejection capture. These have no useful stack and represent
 * event-system noise, not application bugs.
 */
function isCustomEventRejectionNoise(event: ErrorEvent): boolean {
    const errorMessage = event.exception?.values?.[0]?.value || '';
    return (
        errorMessage.includes('CustomEvent') &&
        errorMessage.includes('unhandledrejection') &&
        errorMessage.includes('captured as promise rejection')
    );
}

/**
 * Drop events whose stack frames all come from third-party scripts (nothing from
 * our host bundles or CDN MFE bundles).
 *
 * Replaces `thirdPartyErrorFilterIntegration`, which classifies frames by the
 * Sentry bundler plugin's `applicationKey` metadata. We don't use that plugin, so
 * the integration treated every frame as third-party and dropped all stack-bearing
 * events, host and MFE alike.
 *
 * Events with no frame filenames (stackless rejections, native-only stacks) can't
 * be classified, so they are kept and left to the other filters.
 */
function isExclusivelyThirdPartyStack(event: ErrorEvent): boolean {
    const filenames = (event.exception?.values || [])
        .flatMap(exceptionValue => exceptionValue.stacktrace?.frames || [])
        .map(frame => frame.filename)
        .filter((filename): filename is string => !!filename);

    return filenames.length > 0 && !filenames.some(isFirstPartyFrameFilename);
}

// ---------------------------------------------------------------------------
// Dev helpers
// ---------------------------------------------------------------------------

function isSentryDevEnabled(): boolean {
    return localStorage.getItem('sentryDevEnabled') === 'true';
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export function sentryInit(config: SentryConfig): void {
    Sentry.init({
        environment: config.Environment,
        dsn: config.Dsn,
        release: config.Release,
        // Sends request headers and IP for users.
        // Ensure this aligns with your privacy policy / GDPR posture.
        sendDefaultPii: true,
        integrations: [
            Sentry.httpClientIntegration(),
            // Reads MFE `module_metadata` (app + release) from remote bundles; no multiplex DSN.
            moduleMetadataIntegration(),
        ],
        denyUrls: DENY_URLS,
        ignoreErrors: IGNORE_ERRORS,
        // 50 breadcrumbs is sufficient context for most errors. Reducing from
        // the 100 default cuts event payload size on UpdatePanel-heavy pages
        // where XHR and DOM breadcrumbs accumulate quickly per postback cycle.
        maxBreadcrumbs: 50,
        beforeBreadcrumb(breadcrumb) {
            // Drop XHR/fetch breadcrumbs for ASP.NET WebForms infrastructure
            // endpoints. ScriptResource.axd and WebResource.axd fire on every
            // UpdatePanel postback and have no debugging value for JS errors.
            if (breadcrumb.type === 'http') {
                const url = breadcrumb.data?.url || '';
                if (url.includes('ScriptResource.axd') || url.includes('WebResource.axd')) {
                    return null;
                }

                if (hasPendoToken(url)) {
                    return null;
                }
            }

            if (breadcrumb.type === 'default' || breadcrumb.type === 'error') {
                const message = breadcrumb.message || '';
                const category = breadcrumb.category || '';
                if (hasPendoToken(message) || hasPendoToken(category)) {
                    return null;
                }
            }

            return breadcrumb;
        },
        beforeSend(event) {
            const eventSummary = event.exception?.values?.[0]?.value || event.message || '(no message)';
            const drop = (rule: string) => {
                if (isSentryDevEnabled()) {
                    console.info(`[Sentry] Event filtered — rule: ${rule} | event: ${eventSummary}`);
                }
                return null;
            };

            if (isPendoDependencyFrameError(event)) return drop('isPendoDependencyFrameError');
            if (isAssistDependencyFrameError(event)) return drop('isAssistDependencyFrameError');
            if (isLikelyPendoAssistNoise(event)) return drop('isLikelyPendoAssistNoise');
            if (isNetworkConnectivityError(event)) return drop('isNetworkConnectivityError');
            if (isChunkLoadError(event)) return drop('isChunkLoadError');
            if (isResizeObserverNoise(event)) return drop('isResizeObserverNoise');
            if (isPageRequestManagerNoise(event)) return drop('isPageRequestManagerNoise');
            if (isExtensionError(event)) return drop('isExtensionError');
            if (isCustomEventRejectionNoise(event)) return drop('isCustomEventRejectionNoise');
            if (isNonErrorRejectionNoise(event)) return drop('isNonErrorRejectionNoise');
            if (isExclusivelyThirdPartyStack(event)) return drop('isExclusivelyThirdPartyStack');

            if (event?.exception?.values?.[0].stacktrace?.frames) {
                const frames = event.exception.values[0].stacktrace.frames;
                const mfeMetadata = frames
                    .filter((frame) => frame.module_metadata && frame.module_metadata.app)
                    .map((frame) => frame.module_metadata)
                    .slice(-1);

                if (mfeMetadata.length) {
                    const { app, release } = mfeMetadata[0];
                    event.tags = { ...event.tags, App: app };
                    if (release) {
                        event.release = release;
                    }
                }
            }

            return event;
        },
    });
    Sentry.setTags({
        "App": "CertifyApp-js",
    });
}
